import asyncSeries from 'async/series';
import { ethers } from 'ethers';

import CoinStore from '../stores/coin_store';
import SwapStore from '../stores/swap_store';
import WalletStore from '../stores/wallet_store';

import CoinTools from '../tools/coin_tools';
import { isSameAddress } from '../tools/eth_tools';
import SwapTools from '../tools/swap_tools';
import util from '../tools/util';
const { BigNumber } = ethers;

export default {
  getTotals,
  createOrder,
  cancelOrder,
  settleOrder,
};

const ZERO = '0x0000000000000000000000000000000000000000';
//const APPROVE_VALUE = ethers.BigNumber.from(2).pow(255);
const RATIO = BigNumber.from(10).pow(38).mul(3);
const BPS = 1000;

function getTotals(params) {
  const { token, quantity, currency, price, is_sell } = params;
  const { chain_id } = params.token;
  const quantity_wei = CoinStore.weiAmount(quantity, token);
  const token_wei = CoinStore.weiAmount(1, token);
  const price_wei = CoinStore.weiAmount(price, currency);
  const swap_addr = SwapStore.getSwapContract(chain_id);
  const { token_addr, currency_addr } = SwapStore.sortTokens(params);
  const fee_bps = SwapStore.getFeeBps(chain_id);

  const is_flipped = !isSameAddress(token_addr, token.contract_addr);
  const { price_bucket, price_exp, price_ratio } = _makePrice(
    price_wei,
    token_wei,
    is_flipped
  );

  const flipped_quantity_wei = quantity_wei.mul(RATIO).div(price_ratio);
  let swap_quantity_wei = is_flipped ? flipped_quantity_wei : quantity_wei;
  const currency_wei = swap_quantity_wei.mul(price_ratio).div(RATIO);

  const fee_add = !is_sell;
  const is_spend_token = xor(is_sell, is_flipped);
  let spend_wei = is_spend_token ? swap_quantity_wei : currency_wei;
  const fee_wei = spend_wei.mul(fee_bps).div(BPS);
  if (fee_add) {
    spend_wei = spend_wei.add(fee_wei);
    const fee_quantity_wei = swap_quantity_wei.mul(fee_bps).div(BPS);
    swap_quantity_wei = swap_quantity_wei.add(fee_quantity_wei);
  }
  const total_wei = is_sell ? 0 : spend_wei;

  return {
    token_addr,
    currency_addr,
    swap_addr,
    price_bucket,
    price_exp,
    is_spend_token,
    spend_contract_addr: is_spend_token ? token_addr : currency_addr,
    fee_wei,
    spend_wei,
    swap_quantity_wei,
    total_wei,
  };
}
function createOrder(params, done) {
  const { chain_id } = params.token;
  const {
    token_addr,
    currency_addr,
    swap_addr,
    price_bucket,
    price_exp,
    is_spend_token,
    spend_contract_addr,
    spend_wei,
    swap_quantity_wei,
  } = getTotals(params);
  const auth_opts = {
    chain_id,
    contract_addr: spend_contract_addr,
    owner: WalletStore.getAddress(),
    spender: swap_addr,
    value: spend_wei,
  };
  function preflight(done) {
    return _checkAuthorized(auth_opts, done);
  }

  let approve_tx;
  let is_approved;
  let order_tx;
  asyncSeries(
    [
      (done) => {
        if (auth_opts.contract_addr === ZERO) {
          done();
        } else {
          const opts = { ...auth_opts }; //value: APPROVE_VALUE };
          CoinTools.approve(opts, (err, tx) => {
            if (err) {
              util.errorLog('OrderHelper.createOrder: set err:', err);
            }
            approve_tx = tx;
            done(err);
          });
        }
      },
      (done) => {
        if (auth_opts.contract_addr === ZERO) {
          is_approved = true;
          done();
        } else {
          preflight((err, approved) => {
            if (err) {
              util.errorLog('OrderHelper.createOrder: check err:', err);
            }
            is_approved = approved;
            done(err);
          });
        }
      },
      (done) => {
        const opts = {
          chain_id,
          contract_addr: swap_addr,
          token_addr,
          currency_addr,
          price_bucket,
          price_exp,
          start_block: 0,
          end_block: 10e8,
          quantity: swap_quantity_wei,
          spend_wei,
        };
        const func = is_spend_token ? SwapTools.postSell : SwapTools.postBuy;
        func(opts, (err, tx) => {
          if (err) {
            util.errorLog('OrderStore.createOrder: offer err:', err);
          }
          order_tx = tx;
          done(err);
        });
      },
    ],
    (err) => {
      const data = {
        step_list: [
          {
            text: 'Post your order on the Harbour Swap contract.',
            tx: order_tx,
          },
        ],
      };
      if (spend_contract_addr !== ZERO) {
        data.step_list.unshift(
          {
            text: 'Approve Harbour Swap to withdraw your coins.',
            tx: approve_tx,
            preflight,
            is_complete: is_approved,
          },
          {
            text: 'Confirm approval.',
            confirm_wait_count: 2,
            chain_id,
            timeout: 15 * 1000,
          }
        );
      }
      done(err, data);
    }
  );
}
function cancelOrder(order, done) {
  const { chain_id } = order;
  const swap_addr = SwapStore.getSwapContract(chain_id);
  function success(done) {
    order.is_cancel = true;
    done && done();
  }
  const opts = {
    chain_id,
    contract_addr: swap_addr,
    token_addr: order.token_addr,
    currency_addr: order.currency_addr,
    price_ratio: order.price_ratio,
    index: order.order_index,
  };
  const func = order.is_sell ? SwapTools.cancelSell : SwapTools.cancelBuy;
  func(opts, (err, tx) => {
    let data;
    if (err) {
      util.errorLog('OrderHelper.cancelOrder: err:', err);
    } else {
      data = {
        step_list: [
          {
            text: 'Cancel your order',
            tx,
          },
        ],
        success,
      };
    }
    done(err, data);
  });
}
function settleOrder(order, done) {
  const { chain_id } = order;
  const swap_addr = SwapStore.getSwapContract(chain_id);

  function success(done) {
    order.is_settled = true;
    done && done();
  }
  const opts = {
    chain_id,
    contract_addr: swap_addr,
    token_addr: order.token_addr,
    currency_addr: order.currency_addr,
    price_ratio: order.price_ratio,
  };

  let market;
  let tx;
  asyncSeries(
    [
      (done) => {
        SwapTools.fetchMarket(opts, (err, data) => {
          if (err) {
            util.errorLog('OrderHelper.settleOrder: err:', err);
          } else if (!data.settle_direction) {
            err = 'no_settle';
          }
          market = data;
          done(err);
        });
      },
      (done) => {
        opts.settle_block = market.settle_block;
        let func;
        if (market.settle_direction === 'exact') {
          func = SwapTools.settleBucketExact;
        } else if (market.settle_direction === 'sell') {
          func = SwapTools.settleBucketFlatSell;
        } else {
          func = SwapTools.settleBucketFlatBuy;
        }
        func(opts, (err, new_tx) => {
          if (err) {
            util.errorLog('OrderHelper.settleOrder: err:', err);
          } else {
            tx = new_tx;
          }
          done(err);
        });
      },
    ],
    (err) => {
      let data;
      if (!err) {
        data = {
          step_list: [
            {
              text: 'Settle your order',
              tx,
            },
          ],
          success,
        };
      }
      done(err, data);
    }
  );
}
function _makePrice(price_wei, token_wei, is_flipped) {
  let price_ratio;
  if (is_flipped) {
    price_ratio = token_wei.mul(RATIO).div(price_wei);
  } else {
    price_ratio = price_wei.mul(RATIO).div(token_wei);
  }
  let price_exp = 0;
  let price_bucket = price_ratio;
  while (price_bucket.gt(255)) {
    price_exp++;
    price_bucket = price_bucket.div(10);
  }
  return {
    price_ratio,
    price_bucket: price_bucket.toNumber(),
    price_exp,
  };
}
function _checkAuthorized(params, done) {
  const { value } = params;
  CoinTools.allowance(params, (err, allowance) => {
    let is_approved = !err && allowance && allowance.gte(value);
    done(err, is_approved);
  });
}
function xor(a, b): boolean {
  return Boolean(a ? !b : b);
}
